import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Check, RefreshCw, ShieldCheck, X } from 'lucide-react';
import { ApprovalStatus, PolicyKind, VENUE_EVENTS } from '@/lib/shared-types';
import { api } from '@/lib/api';
import { useApi, rowsOf } from '@/Hooks/useApi';
import { useAuth } from '@/Hooks/useAuth';
import { usePermission } from '@/Hooks/usePermission';
import { useRealtime } from '@/Hooks/useRealtime';
import { useBranchRooms } from '@/Pages/Operations/shared';
import { useAppSelector } from '@/Redux/store';
import { Badge, toneForStatus } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog } from '@/components/ui/dialog';
import { Field, Select, Textarea } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { TableWrap, Table, THead, TH, TR, TD, TableEmpty } from '@/components/ui/table';
import {
  Amount,
  ApprovalDetail,
  PAYLOAD_FIELDS,
  payloadValue,
  type PolicyApproval,
} from './ApprovalDetail';
import {
  apiMessage,
  apiStatus,
  countdown,
  formatDateTime,
  personName,
  useNow,
  useQueryParams,
} from './lib';

/** The five §24.5 gates, straight off the Prisma `PolicyKind` enum. */
const POLICY_KINDS: PolicyKind[] = Object.values(PolicyKind);

/**
 * C-13 — signing authority is per policy kind, mirroring what the API enforces
 * in `PolicyApprovalsController`. `approvals.view` gets you the queue;
 * `approvals.approve` signs anything, and each `approve_<kind>` signs one gate.
 * Presentation only, as always — the server is the one that refuses.
 */
const APPROVE_PERMISSION: Record<PolicyKind, string> = {
  DISCOUNT: 'approvals.approve_discount',
  VOID: 'approvals.approve_void',
  REFUND: 'approvals.approve_refund',
  PRICE_OVERRIDE: 'approvals.approve_price_override',
  STOCK_ADJUSTMENT: 'approvals.approve_stock_adjustment',
};

const APPROVAL_STATUSES: ApprovalStatus[] = Object.values(ApprovalStatus);

/**
 * §24.5 — the manager approval queue.
 *
 * Everything here is a decision somebody is *waiting on*: a cashier cannot
 * refund, a waiter cannot discount and a stockkeeper cannot write off until a
 * second person signs. The queue therefore defaults to PENDING and sorts oldest
 * first, and every row carries its expiry — an unanswered request lapses after
 * about a shift and has to be made again.
 *
 * Self-approval is refused server-side with a 403 (the whole point of the gate
 * is a second signature). The buttons are disabled with that explanation rather
 * than left live to fail, so nobody learns the rule from an error toast.
 *
 * Frame `64:2855` is one row opened in full — `ApprovalDetail` — reached from
 * the reason cell. The queue itself keeps its filters, its oldest-first order,
 * its live expiry countdown, its two realtime events and its per-branch money,
 * none of which the frame draws.
 */
export default function Approvals() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { canAny } = usePermission();
  const now = useNow(1000);
  const activeBranchId = useAppSelector((s) => s.ui.activeBranchId);

  const [status, setStatus] = useState<string>('PENDING');
  const [policy, setPolicy] = useState('');
  const [search, setSearch] = useState('');
  const [decision, setDecision] = useState<{
    kind: 'approve' | 'reject';
    approval: PolicyApproval;
  } | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);

  const maySign = useCallback(
    (policy: PolicyKind) => canAny('approvals.approve', APPROVE_PERMISSION[policy]),
    [canAny],
  );

  const params = useQueryParams({
    status,
    policy,
    search,
    limit: 100,
    sortBy: 'requestedAt',
    // Oldest first: the person who has been waiting longest is answered first.
    sortOrder: status === 'PENDING' ? 'asc' : 'desc',
  });

  const { data, loading, error, reload } = useApi<unknown>('/policy-approvals', params);
  const approvals = useMemo(() => rowsOf<PolicyApproval>(data).rows, [data]);
  const openApproval = useMemo(
    () => approvals.find((row) => row.id === openId) ?? null,
    [approvals, openId],
  );

  // ------------------------------------------------------------- realtime

  /**
   * A §24.5 request is announced in its branch room (and in the requester's own
   * user room, which the socket joins from the token). A manager is not in a
   * branch room until asked, so join the branches this queue is actually about
   * plus whichever branch the header has selected.
   */
  const branchRooms = useBranchRooms(activeBranchId, approvals);

  /**
   * `policy_approval.*`, and the `kind === 'POLICY'` filter that used to sit here
   * is gone with the events it was reading.
   *
   * The queue used to borrow `spending_approval.*` from the guest host queue, so
   * both kinds of request arrived on the same two names and this screen had to
   * sort them out by a discriminator on the payload. The host queue went with
   * Party; the gate has its own names and its own payload now, every frame on
   * them belongs on this screen, and the discriminator no longer exists — left
   * in place it would have matched nothing and quietly stopped the queue
   * refreshing at all.
   */
  const onApprovalEvent = useCallback(() => reload(), [reload]);

  useRealtime(VENUE_EVENTS.POLICY_APPROVAL_REQUESTED, onApprovalEvent, branchRooms);
  useRealtime(VENUE_EVENTS.POLICY_APPROVAL_RESOLVED, onApprovalEvent, branchRooms);

  // A lapsed request stops being actionable without anything arriving over the
  // socket, so sweep the view once anything on screen crosses its deadline.
  const nextDeadline = useMemo(
    () =>
      approvals
        .filter((a) => a.status === 'PENDING' && a.expiresAt)
        .reduce<number | null>((soonest, a) => {
          const at = new Date(a.expiresAt as string).getTime();
          if (Number.isNaN(at)) return soonest;
          return soonest === null || at < soonest ? at : soonest;
        }, null),
    [approvals],
  );

  useEffect(() => {
    if (nextDeadline === null || status !== 'PENDING') return;
    const delay = nextDeadline - Date.now();
    if (delay <= 0) return;
    const id = window.setTimeout(reload, delay + 500);
    return () => window.clearTimeout(id);
  }, [nextDeadline, status, reload]);

  // --------------------------------------------------------------- actions

  const closeDecision = () => {
    setDecision(null);
    setNote('');
  };

  const submitDecision = async () => {
    if (!decision) return;
    const { kind, approval } = decision;
    setBusy(true);
    try {
      await api.post(`/policy-approvals/${approval.id}/${kind}`, {
        note: note.trim() || undefined,
      });
      toast.success(t(kind === 'approve' ? 'platform.approvals.approved' : 'platform.approvals.rejected'));
      closeDecision();
      setOpenId(null);
      reload();
    } catch (e) {
      // 403 is self-approval, 409 is "already answered or expired". Both are
      // states the queue can be re-read out of, so refresh either way.
      const code = apiStatus(e);
      toast.error(apiMessage(e, t('platform.approvals.actionFailed')));
      if (code === 409 || code === 403) reload();
    } finally {
      setBusy(false);
    }
  };

  // ---------------------------------------------------------------- render

  const pendingCount = approvals.filter((a) => a.status === 'PENDING').length;

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-semibold">
            <Icon as={ShieldCheck} size="lg" />
            {t('platform.approvals.title')}
          </h1>
          <p className="text-sm text-ink-muted">
            {t('platform.approvals.subtitle')}
          </p>
        </div>

        <div className="flex items-center gap-2">
          {status === 'PENDING' && pendingCount > 0 && (
            <Badge tone="warning">
              {t('platform.approvals.waitingCount', { count: pendingCount })}
            </Badge>
          )}
          <Button variant="outline" size="sm" onClick={reload} disabled={loading}>
            <Icon as={RefreshCw} />
            {t('platform.approvals.refresh')}
          </Button>
        </div>
      </header>

      <Card>
        <CardHeader className="pb-0">
          <CardTitle className="sr-only">{t('platform.approvals.filters')}</CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <Field label={t('platform.approvals.status')}>
              <Select value={status} onChange={(e) => setStatus(e.target.value)}>
                {APPROVAL_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {t(`platform.approvals.statusLabel.${s}`)}
                  </option>
                ))}
              </Select>
            </Field>

            <Field label={t('platform.approvals.policy')}>
              <Select value={policy} onChange={(e) => setPolicy(e.target.value)}>
                <option value="">{t('platform.approvals.allPolicies')}</option>
                {POLICY_KINDS.map((p) => (
                  <option key={p} value={p}>
                    {t(`platform.approvals.policyLabel.${p}`)}
                  </option>
                ))}
              </Select>
            </Field>

            <Field label={t('platform.approvals.searchReason')}>
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('platform.approvals.searchReasonHint')}
              />
            </Field>
          </div>
        </CardContent>
      </Card>

      {loading && !approvals.length ? (
        <LoadingState label={t('platform.approvals.loading')} />
      ) : error ? (
        <ErrorState message={error} onRetry={reload} />
      ) : (
        <TableWrap>
          <Table>
            <THead>
              <TR>
                <TH>{t('platform.approvals.requested')}</TH>
                <TH>{t('platform.approvals.requester')}</TH>
                <TH>{t('platform.approvals.policy')}</TH>
                <TH>{t('platform.approvals.request')}</TH>
                <TH className="text-end">{t('platform.approvals.amount')}</TH>
                <TH>{t('platform.approvals.expiresIn')}</TH>
                <TH className="text-end">{t('platform.approvals.decision')}</TH>
              </TR>
            </THead>
            <tbody>
              {!approvals.length && (
                <TableEmpty colSpan={7}>
                  <EmptyState
                    title={t('platform.approvals.empty')}
                    hint={t('platform.approvals.emptyHint')}
                  />
                </TableEmpty>
              )}

              {approvals.map((approval) => {
                const isOwn = approval.requestedById === user?.id;
                const left = countdown(approval.expiresAt, now);
                const lapsed = approval.status === 'PENDING' && left?.expired;
                const mayResolve = maySign(approval.policy);
                const actionable = approval.status === 'PENDING' && !lapsed && mayResolve && !isOwn;
                const fields = PAYLOAD_FIELDS[approval.policy] ?? [];

                return (
                  <TR key={approval.id}>
                    <TD className="whitespace-nowrap text-ink-muted">
                      {formatDateTime(approval.requestedAt, i18n.language)}
                      {approval.branch && (
                        <div className="text-xs">{approval.branch.name}</div>
                      )}
                    </TD>

                    <TD className="whitespace-nowrap">
                      {personName(approval.requestedBy)}
                      {isOwn && (
                        <div className="text-xs text-ink-muted">
                          {t('platform.approvals.you')}
                        </div>
                      )}
                    </TD>

                    <TD className="whitespace-nowrap">
                      <Badge tone={toneForStatus(approval.status)}>
                        {t(`platform.approvals.policyLabel.${approval.policy}`)}
                      </Badge>
                      {approval.status !== 'PENDING' && (
                        <div className="mt-1 text-xs text-ink-muted">
                          {t(`platform.approvals.statusLabel.${approval.status}`)}
                          {approval.resolvedBy && ` · ${personName(approval.resolvedBy)}`}
                        </div>
                      )}
                    </TD>

                    <TD className="max-w-md">
                      <button
                        type="button"
                        className="text-start font-medium underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        onClick={() => setOpenId(approval.id)}
                      >
                        {approval.reason}
                      </button>
                      <p className="text-xs text-ink-muted">
                        {approval.entityType}
                        {approval.entityId ? ` · ${approval.entityId}` : ''}
                      </p>
                      {!!approval.payload && (
                        <dl className="mt-1 grid gap-x-3 text-xs sm:grid-cols-2">
                          {fields
                            .filter((f) => approval.payload?.[f] != null)
                            .map((f) => (
                              <div key={f} className="flex gap-1">
                                <dt className="text-ink-muted">
                                  {t(`platform.approvals.payload.${f}`)}
                                </dt>
                                <dd className="truncate">
                                  {payloadValue(f, approval.payload?.[f])}
                                </dd>
                              </div>
                            ))}
                        </dl>
                      )}
                      {approval.resolutionNote && (
                        <p className="mt-1 text-xs italic text-ink-muted">
                          “{approval.resolutionNote}”
                        </p>
                      )}
                    </TD>

                    <TD className="text-end">
                      <Amount amount={approval.amount} branchId={approval.branchId} />
                    </TD>

                    <TD className="whitespace-nowrap">
                      {approval.status !== 'PENDING' ? (
                        <span className="text-ink-muted">
                          {formatDateTime(approval.resolvedAt, i18n.language)}
                        </span>
                      ) : !left ? (
                        <span className="text-ink-muted">
                          {t('platform.approvals.noExpiry')}
                        </span>
                      ) : left.expired ? (
                        <Badge tone="danger">{t('platform.approvals.lapsed')}</Badge>
                      ) : (
                        <span
                          className={
                            left.urgent ? 'font-medium tabular-nums text-red-600 dark:text-red-400' : 'tabular-nums'
                          }
                        >
                          {left.label}
                        </span>
                      )}
                    </TD>

                    <TD>
                      <div className="flex flex-wrap justify-end gap-1.5">
                        {approval.status !== 'PENDING' || lapsed ? (
                          <span className="text-xs text-ink-muted">
                            {lapsed
                              ? t('platform.approvals.lapsedHint')
                              : t('platform.approvals.alreadyAnswered')}
                          </span>
                        ) : !mayResolve ? (
                          <span className="text-xs text-ink-muted">
                            {t('platform.approvals.notPermitted')}
                          </span>
                        ) : (
                          <>
                            <Button
                              size="sm"
                              disabled={!actionable}
                              title={isOwn ? t('platform.approvals.selfBlocked') : undefined}
                              onClick={() => setDecision({ kind: 'approve', approval })}
                            >
                              <Icon as={Check} />
                              {t('platform.approvals.approve')}
                            </Button>
                            <Button
                              size="sm"
                              variant="danger"
                              disabled={!actionable}
                              title={isOwn ? t('platform.approvals.selfBlocked') : undefined}
                              onClick={() => setDecision({ kind: 'reject', approval })}
                            >
                              <Icon as={X} />
                              {t('platform.approvals.reject')}
                            </Button>
                          </>
                        )}
                      </div>
                      {isOwn && approval.status === 'PENDING' && !lapsed && mayResolve && (
                        <p className="mt-1 text-end text-xs text-ink-muted">
                          {t('platform.approvals.selfBlocked')}
                        </p>
                      )}
                    </TD>
                  </TR>
                );
              })}
            </tbody>
          </Table>
        </TableWrap>
      )}

      <ApprovalDetail
        approval={openApproval}
        now={now}
        maySign={openApproval ? maySign(openApproval.policy) : false}
        isOwn={openApproval?.requestedById === user?.id}
        onDecide={(kind, approval) => setDecision({ kind, approval })}
        onClose={() => setOpenId(null)}
      />

      <Dialog
        open={!!decision}
        onClose={closeDecision}
        title={
          decision?.kind === 'reject'
            ? t('platform.approvals.rejectTitle')
            : t('platform.approvals.approveTitle')
        }
        description={
          decision?.kind === 'reject'
            ? t('platform.approvals.rejectHint')
            : t('platform.approvals.approveHint')
        }
        footer={
          <>
            <Button variant="outline" onClick={closeDecision} disabled={busy}>
              {t('common.cancel')}
            </Button>
            <Button
              variant={decision?.kind === 'reject' ? 'danger' : 'default'}
              onClick={() => void submitDecision()}
              disabled={busy}
            >
              {decision?.kind === 'reject'
                ? t('platform.approvals.reject')
                : t('platform.approvals.approve')}
            </Button>
          </>
        }
      >
        {decision && (
          <div className="space-y-4">
            <div className="rounded-lg border p-3 text-sm">
              <p className="font-medium">{decision.approval.reason}</p>
              <p className="mt-1 text-ink-muted">
                {t(`platform.approvals.policyLabel.${decision.approval.policy}`)} ·{' '}
                {personName(decision.approval.requestedBy)} ·{' '}
                {formatDateTime(decision.approval.requestedAt, i18n.language)}
              </p>
              <p className="mt-1">
                <Amount
                  amount={decision.approval.amount}
                  branchId={decision.approval.branchId}
                />
              </p>
            </div>

            <Field
              label={t('platform.approvals.note')}
              hint={
                decision.kind === 'reject'
                  ? t('platform.approvals.noteRejectHint')
                  : t('platform.approvals.noteApproveHint')
              }
            >
              <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
            </Field>
          </div>
        )}
      </Dialog>
    </div>
  );
}
